import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import CurrencyInput from './CurrencyInput';

interface CashFlowTableProps {
  projection: any;
  calculated: any;
  onChange: (field: string, value: string | number) => void;
}

export default function CashFlowTable({ projection, calculated, onChange }: CashFlowTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Proyeksi Arus Kas</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-3 text-left font-medium">POSISI KAS & BANK</th>
                <th className="border p-3 text-center font-medium w-48">SEBELUM PENCAIRAN</th>
                <th className="border p-3 text-center font-medium w-48">SETELAH PENCAIRAN</th>
              </tr>
            </thead>
            <tbody>
              {/* Saldo Awal Kas & Bank */}
              <tr className="bg-gray-50">
                <td className="border p-3 font-semibold">Saldo Awal Kas & Bank</td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.saldoAwalBefore} readOnly />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Kas</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.kas_before}
                    onChange={value => onChange('kas_before', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Tabungan</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.tabungan_before}
                    onChange={value => onChange('tabungan_before', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Giro</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.giro_before}
                    onChange={value => onChange('giro_before', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>

              {/* Saldo Kas & Bank Minimal */}
              <tr className="bg-gray-50">
                <td className="border p-3 font-semibold">
                  Saldo Kas & Bank Minimal Dari Bulan ke Bulan
                </td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.saldoAwalBefore} readOnly />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>

              {/* KETERANGAN ARUS KAS */}
              <tr className="bg-blue-50">
                <td className="border p-3 font-semibold" colSpan={3}>
                  KETERANGAN ARUS KAS
                </td>
              </tr>

              {/* a. Saldo Awal Operasional */}
              <tr className="bg-gray-50">
                <td className="border p-3 font-semibold">a. Saldo Awal Operasional</td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.saldoAwalOpBefore} readOnly />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.kas_after}
                    onChange={value => onChange('kas_after', value)}
                  />
                </td>
              </tr>

              {/* b. Arus Kas Masuk */}
              <tr className="bg-green-50">
                <td className="border p-3 font-semibold">b. Arus Kas Masuk</td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.arusKasMasukBefore} readOnly />
                </td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.arusKasMasukAfter} readOnly />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Gaji</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.gaji_before}
                    onChange={value => onChange('gaji_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.gaji_after}
                    onChange={value => onChange('gaji_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Pendapatan Usaha (Omzet Penjualan)</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.pendapatan_usaha_before}
                    onChange={value => onChange('pendapatan_usaha_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.pendapatan_usaha_after}
                    onChange={value => onChange('pendapatan_usaha_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Pendapatan Lainnya</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.pendapatan_lainnya_before}
                    onChange={value => onChange('pendapatan_lainnya_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.pendapatan_lainnya_after}
                    onChange={value => onChange('pendapatan_lainnya_after', value)}
                  />
                </td>
              </tr>

              {/* c. Arus Kas Keluar */}
              <tr className="bg-red-50">
                <td className="border p-3 font-semibold">c. Arus Kas Keluar</td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.arusKasKeluarBefore} readOnly />
                </td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.arusKasKeluarAfter} readOnly />
                </td>
              </tr>
              <tr className="bg-purple-50">
                <td className="border p-3 pl-8 text-purple-700 font-medium">
                  Penambahan Modal Kerja dan Persediaan Penjualan
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.penambahan_modal_before}
                    onChange={value => onChange('penambahan_modal_before', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Beban Angsuran Hutang Bank Lain</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.angsuran_bank_lain_before}
                    onChange={value => onChange('angsuran_bank_lain_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.angsuran_bank_lain_after}
                    onChange={value => onChange('angsuran_bank_lain_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-8">Beban Rumah Tangga</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_rumah_tangga_before}
                    onChange={value => onChange('beban_rumah_tangga_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_rumah_tangga_after}
                    onChange={value => onChange('beban_rumah_tangga_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">
                  Beban Biaya Hidup (Biaya Makan, Belanja Konsumsi, dsb)
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_makan_before}
                    onChange={value => onChange('beban_makan_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_makan_after}
                    onChange={value => onChange('beban_makan_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">
                  Beban Listrik, Air, dan Utilitas Lainnya
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_listrik_air_before}
                    onChange={value => onChange('beban_listrik_air_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_listrik_air_after}
                    onChange={value => onChange('beban_listrik_air_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">Beban Pendidikan Anggota Keluarga</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_pendidikan_before}
                    onChange={value => onChange('beban_pendidikan_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_pendidikan_after}
                    onChange={value => onChange('beban_pendidikan_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">Beban Telekomunikasi</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_telekomunikasi_before}
                    onChange={value => onChange('beban_telekomunikasi_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_telekomunikasi_after}
                    onChange={value => onChange('beban_telekomunikasi_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">Beban Transportasi</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_transportasi_before}
                    onChange={value => onChange('beban_transportasi_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_transportasi_after}
                    onChange={value => onChange('beban_transportasi_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">
                  Beban Listrik, Air, dan Utilitas Lainnya
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_listrik_air_2_before}
                    onChange={value => onChange('beban_listrik_air_2_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_listrik_air_2_after}
                    onChange={value => onChange('beban_listrik_air_2_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">Beban Sewa</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_sewa_before}
                    onChange={value => onChange('beban_sewa_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_sewa_after}
                    onChange={value => onChange('beban_sewa_after', value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm">Beban Lainnya</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_lainnya_before}
                    onChange={value => onChange('beban_lainnya_before', value)}
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_lainnya_after}
                    onChange={value => onChange('beban_lainnya_after', value)}
                  />
                </td>
              </tr>

              {/* d. Arus Kas Bersih */}
              <tr className="bg-pink-50">
                <td className="border p-3 font-semibold">d. Arus Kas Bersih (b - c)</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.arusKasBersihBefore}
                    readOnly
                    className="text-right bg-gray-100 font-semibold"
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.arusKasBersihAfter}
                    readOnly
                    className="text-right bg-gray-100 font-semibold"
                  />
                </td>
              </tr>

              {/* e. Saldo Akhir Operasional */}
              <tr className="bg-gray-50">
                <td className="border p-3 font-semibold">e. Saldo Akhir Operasional (a + d)</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoAkhirOpBefore}
                    readOnly
                    className="text-right bg-gray-100 font-semibold"
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoAkhirOpAfter}
                    readOnly
                    className="text-right bg-gray-100 font-semibold"
                  />
                </td>
              </tr>

              {/* f. Realisasi Kredit */}
              <tr>
                <td className="border p-3 font-medium">
                  f. Realisasi Kredit / Pembiayaan dari BPR
                </td>
                <td className="border p-3 bg-gray-50"></td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr className="bg-orange-50">
                <td className="border p-3 pl-8 text-orange-700">
                  f1. Beban Realisasi Kredit / Pembiayaan
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_realisasi_kredit}
                    onChange={value => onChange('beban_realisasi_kredit', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">
                  Beban Provisi ({projection.beban_provisi_pct}%)
                </td>
                <td className="border p-3">
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={projection.beban_provisi_pct ?? ''}
                      onChange={e =>
                        onChange('beban_provisi_pct', e.target.value === '' ? '' : e.target.value)
                      }
                      className="text-right w-20 focus:border-blue-500"
                    />
                    <CurrencyInput value={calculated.bebanProvisi} readOnly />
                  </div>
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">Beban Administrasi</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.beban_admin}
                    onChange={value => onChange('beban_admin', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">Biaya Materai</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.biaya_materai}
                    onChange={value => onChange('biaya_materai', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">Biaya Notaris</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.biaya_notaris}
                    onChange={value => onChange('biaya_notaris', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">Biaya Asuransi</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.biaya_asuransi}
                    onChange={value => onChange('biaya_asuransi', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-700">Biaya Lainnya</td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.biaya_lainnya}
                    onChange={value => onChange('biaya_lainnya', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>

              {/* f2. Kredit yang Diberikan */}
              <tr className="bg-green-50">
                <td className="border p-3 pl-8 text-green-700 font-medium">
                  f2. Kredit / Pembiayaan yang Diberikan (Usulan)
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={projection.kredit_usulan}
                    onChange={value => onChange('kredit_usulan', value)}
                  />
                </td>
                <td className="border p-3 bg-gray-50"></td>
              </tr>

              {/* f3. Beban Cicilan */}
              <tr className="bg-orange-50">
                <td className="border p-3 pl-8 text-orange-700 font-medium">
                  f3. Beban Cicilan / Angsuran (Repayment Capacity ~
                  {projection.repayment_capacity_pct}%)
                </td>
                <td className="border p-3 bg-gray-50"></td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.repaymentCapacityAmount}
                    readOnly
                    className="font-semibold"
                  />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-600">
                  Cicilan Bunga / Biaya Jasa ({projection.suku_bunga_flat}% Flat p.a.)
                </td>
                <td className="border p-3 bg-gray-50"></td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.angsuranBunga} readOnly />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-600">Cicilan Pokok</td>
                <td className="border p-3 bg-gray-50"></td>
                <td className="border p-3">
                  <CurrencyInput value={calculated.angsuranPokok} readOnly />
                </td>
              </tr>
              <tr>
                <td className="border p-3 pl-12 text-sm text-orange-600">
                  Tenor / Jangka Waktu (Bulan)
                </td>
                <td className="border p-3 bg-gray-50"></td>
                <td className="border p-3">
                  <Input type="number" value={projection.tenor_bulan} readOnly />
                </td>
              </tr>

              {/* g. Saldo Akhir Kas & Bank */}
              <tr className="bg-gray-100">
                <td className="border p-3 font-bold">
                  g. Saldo Akhir Kas & Bank (e - f1 + f2 - f3)
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoAkhirKasBefore}
                    readOnly
                    className="font-bold"
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoAkhirKasAfter}
                    readOnly
                    className="font-bold"
                  />
                </td>
              </tr>

              {/* h. Saldo Baki Debet */}
              <tr className="bg-gray-100">
                <td className="border p-3 font-bold">
                  h. Saldo Baki Debet Kredit / Pembiayaan yang Diberikan
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoBakiDebetBefore}
                    readOnly
                    className="font-bold"
                  />
                </td>
                <td className="border p-3">
                  <CurrencyInput
                    value={calculated.saldoBakiDebetAfter}
                    readOnly
                    className="font-bold"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
